
import React, { useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { FaPencilAlt } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { IoEyeSharp } from "react-icons/io5";
import { MyContext } from "../../App";
import { Link } from "react-router-dom";
import { emphasize, styled } from "@mui/material/styles";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { deleteData, fetchDataFromApi } from "../../utils/api";
import * as MuiIcons from "@mui/icons-material";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const Category = () => {
  const [catData, setCatData] = useState([]);
  const [isLoadingBar, setIsLoadingBar] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const context = useContext(MyContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    context.setProgress(20);
    fetchDataFromApi("/api/category").then((res) => {
      setCatData(res);
      context.setProgress(100);
    });
  }, []);

  const getIconComponent = (iconName) => {
    const IconComponent = MuiIcons[iconName];
    return IconComponent ? <IconComponent style={{ fontSize: "24px" }} /> : null;
  };

  const handleOpenConfirm = (id, name) => {
    setDeleteId(id);
    setDeleteName(name);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setDeleteId(null);
    setDeleteName("");
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteCat(deleteId);
    }
    handleCloseConfirm();
  };

  const deleteCat = (id) => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    if (userInfo?.email === "rismicmartpvtltd@gmail.com") {
      setIsLoadingBar(true);
      context.setProgress(30);
      deleteData(`/api/category/${id}`).then(() => {
        context.setProgress(100);
        fetchDataFromApi("/api/category").then((res) => {
          setCatData(res);
          context.setProgress(100);
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Category Deleted Successfully!",
          });
          setIsLoadingBar(false);
        });
      });
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only Admin can delete Category",
      });
    }
  };

  // View details dialog handlers
  const handleOpenDetails = (category) => {
    setSelectedCategory(category);
    setViewDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setViewDetailsDialog(false);
    setSelectedCategory(null);
  };

  // Filter only parent categories (no parentId)
  const parentCategories = catData?.categoryList?.filter(
    (cat) => !cat.parentId
  );

  return (
    <>
      <div className="right-content w-100">
        {/* Header Section */}
        <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
          <h5 className="mb-0">
            <strong>Category Management</strong>
          </h5>

          <div className="ml-auto d-flex align-items-center">
            <Breadcrumbs
              aria-label="breadcrumb"
              className="ml-auto breadcrumbs_"
            >
              <StyledBreadcrumb
                component="a"
                href="#"
                label="Dashboard"
                icon={<HomeIcon fontSize="small" />}
              />

              <StyledBreadcrumb
                label="Category"
                deleteIcon={<ExpandMoreIcon />}
              />
            </Breadcrumbs>

            <Link to="/category/add">
              <Button className="btn-blue ml-3 pl-3 pr-3">
                + Add Category
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="card shadow border-0 p-3">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Categories
                  </Typography>
                  <Typography variant="h4" color="primary">
                    <strong>{parentCategories?.length || 0}</strong>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <MuiIcons.Category fontSize="large" />
                </Box>
              </Box>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card shadow border-0 p-3">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Subcategories
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    <strong>
                      {parentCategories?.reduce(
                        (acc, cat) => acc + (cat?.children?.length || 0),
                        0
                      )}
                    </strong>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <MuiIcons.AccountTree fontSize="large" />
                </Box>
              </Box>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card shadow border-0 p-3">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Active Categories
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    <strong>{parentCategories?.length || 0}</strong>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <MuiIcons.CheckCircle fontSize="large" />
                </Box>
              </Box>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="card shadow border-0 p-3 mt-4">
          <div className="table-responsive mt-3">
            <table className="table table-bordered table-striped table-hover v-align">
              <thead className="thead-dark">
                <tr>
                  <th style={{ width: "100px", textAlign: "center" }}>
                    IMAGE
                  </th>
                  <th style={{ width: "80px", textAlign: "center" }}>ICON</th>
                  <th>CATEGORY NAME</th>
                  <th style={{ width: "120px", textAlign: "center" }}>
                    CARD COLOR
                  </th>
                  <th style={{ width: "120px", textAlign: "center" }}>
                    THEME COLOR
                  </th>
                  <th style={{ width: "100px", textAlign: "center" }}>
                    SUB-CAT
                  </th>
                  <th style={{ width: "200px", textAlign: "center" }}>
                    ACTIONS
                  </th>
                </tr>
              </thead>

              <tbody>
                {parentCategories?.length !== 0 ? (
                  parentCategories
                    ?.slice(0)
                    .reverse()
                    .map((item, index) => (
                      <tr key={index}>
                        {/* Thumbnail Image */}
                        <td style={{ textAlign: "center" }}>
                          <div
                            className="imgWrapper"
                            style={{
                              width: "70px",
                              height: "70px",
                              margin: "0 auto",
                              borderRadius: "8px",
                              overflow: "hidden",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            {item?.images?.length > 0 ? (
                              <LazyLoadImage
                                alt={item.name}
                                effect="blur"
                                className="w-100 h-100"
                                src={item.images[0]}
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  background: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                  color: "#999",
                                }}
                              >
                                No Image
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Icon Display - Custom Image OR Material-UI Icon */}
                        <td style={{ textAlign: "center" }}>
                          <Tooltip
                            title={
                              item?.iconImage
                                ? "Custom Icon Image"
                                : item?.icon || "No Icon"
                            }
                            arrow
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "12px",
                                background: item?.color || "#f5f5f5",
                                borderRadius: "10px",
                                minWidth: "50px",
                                minHeight: "50px",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              }}
                            >
                              {item?.iconImage ? (
                                // Display custom uploaded icon image
                                <img
                                  src={item.iconImage}
                                  alt={item.name}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : item?.icon ? (
                                // Display Material-UI icon
                                getIconComponent(item.icon)
                              ) : (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#999",
                                  }}
                                >
                                  No Icon
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        </td>

                        {/* Category Name */}
                        <td>
                          <div>
                            <Typography
                              variant="body1"
                              className="text-capitalize"
                              style={{ fontWeight: "600", marginBottom: "4px" }}
                            >
                              {item.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ color: "#999" }}
                            >
                              Slug: {item.slug}
                            </Typography>
                          </div>
                        </td>

                        {/* Card Color */}
                        <td style={{ textAlign: "center" }}>
                          <Tooltip title={item?.color || "No Color"} arrow>
                            <div
                              style={{
                                width: "80px",
                                height: "40px",
                                background: item?.color || "#ffffff",
                                border: "2px solid #ddd",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                fontWeight: "600",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              }}
                            >
                              {item?.color ? (
                                <span style={{ fontSize: "9px" }}>
                                  {item.color}
                                </span>
                              ) : (
                                "None"
                              )}
                            </div>
                          </Tooltip>
                        </td>

                        {/* Theme Color */}
                        <td style={{ textAlign: "center" }}>
                          <Tooltip title={item?.themeColor || "No Theme"} arrow>
                            <div
                              style={{
                                width: "80px",
                                height: "40px",
                                background: item?.themeColor || "#ffffff",
                                border: "2px solid #ddd",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                fontWeight: "600",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              }}
                            >
                              {item?.themeColor ? (
                                <span style={{ fontSize: "9px" }}>
                                  {item.themeColor}
                                </span>
                              ) : (
                                "None"
                              )}
                            </div>
                          </Tooltip>
                        </td>

                        {/* Subcategories Count */}
                        <td style={{ textAlign: "center" }}>
                          <Tooltip
                            title={`${item?.children?.length || 0} Subcategories`}
                            arrow
                          >
                            <span
                              className="badge"
                              style={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                padding: "8px 16px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "600",
                              }}
                            >
                              {item?.children?.length || 0}
                            </span>
                          </Tooltip>
                        </td>

                        {/* Actions */}
                        <td>
                          <div
                            className="actions d-flex align-items-center justify-content-center"
                            style={{ gap: "8px" }}
                          >
                            <Tooltip title="View Details" arrow>
                              <Button
                                className="success"
                                color="success"
                                onClick={() => handleOpenDetails(item)}
                                style={{
                                  minWidth: "45px",
                                   
                                  borderRadius: "8px",
                                }}
                              >
                                <IoEyeSharp style={{ fontSize: "24px" }} />
                              </Button>
                            </Tooltip>

                            <Tooltip title="Edit Category" arrow>
                              <Link to={`/category/edit/${item._id}`}>
                                <Button
                                  className="secondary"
                                  color="secondary"
                                  style={{
                                    minWidth: "45px",
                                    
                                    borderRadius: "8px",
                                  }}
                                >
                                  <FaPencilAlt style={{ fontSize: "24px" }} />
                                </Button>
                              </Link>
                            </Tooltip>

                            <Tooltip title="Delete Category" arrow>
                              <Button
                                className="error"
                                color="error"
                                onClick={() =>
                                  handleOpenConfirm(item._id, item.name)
                                }
                                disabled={isLoadingBar}
                                style={{
                                  minWidth: "45px",
                                   
                                  borderRadius: "8px",
                                }}
                              >
                                <MdDelete style={{ fontSize: "24px" }} />
                              </Button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center" style={{ padding: "40px" }}>
                      <MuiIcons.Category
                        style={{ fontSize: "60px", color: "#ddd" }}
                      />
                      <Typography variant="h6" color="textSecondary" style={{ marginTop: "16px" }}>
                        No Categories Found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Start by adding your first category
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <MuiIcons.Warning style={{ color: "#ff9800", fontSize: "32px" }} />
            <Typography variant="h6">Confirm Deletion</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category{" "}
            <strong style={{ color: "#f44336" }}>"{deleteName}"</strong>?
            <br />
            <br />
            <Typography variant="body2" color="error">
              ⚠️ This will also delete all subcategories, products, and related
              data under it. This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseConfirm}
            variant="outlined"
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            autoFocus
            style={{ borderRadius: "8px" }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialog}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "16px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "24px",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <MuiIcons.Info style={{ fontSize: "28px" }} />
            <div>
              <Typography variant="h5" style={{ fontWeight: "600" }}>
                {selectedCategory?.name}
              </Typography>
              <Typography variant="caption" style={{ opacity: 0.9 }}>
                Category Details & Information
              </Typography>
            </div>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: "32px" }}>
          {selectedCategory && (
            <div className="category-details">
              {/* Basic Info */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Basic Information
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Category Name
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: "600" }}>
                      {selectedCategory?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Slug
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: "600" }}>
                      {selectedCategory?.slug}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Subcategories Count
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: "600" }}>
                      {selectedCategory?.children?.length || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Colors Section */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Color Scheme
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Card Background Color
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <div
                        style={{
                          width: "100px",
                          height: "50px",
                          background: selectedCategory?.color || "#ffffff",
                          border: "2px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      ></div>
                      <Typography
                        variant="body2"
                        style={{
                          background: "#f5f5f5",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          fontFamily: "monospace",
                        }}
                      >
                        {selectedCategory?.color || "None"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Theme Color
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <div
                        style={{
                          width: "100px",
                          height: "50px",
                          background: selectedCategory?.themeColor || "#ffffff",
                          border: "2px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      ></div>
                      <Typography
                        variant="body2"
                        style={{
                          background: "#f5f5f5",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          fontFamily: "monospace",
                        }}
                      >
                        {selectedCategory?.themeColor || "None"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Icon Section - UPDATED */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Category Icon
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  padding="16px 24px"
                  style={{
                    background: selectedCategory?.color || "#f5f5f5",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {selectedCategory?.iconImage ? (
                    // Display custom uploaded icon image
                    <>
                      <img
                        src={selectedCategory.iconImage}
                        alt={selectedCategory.name}
                        style={{
                          width: "48px",
                          height: "48px",
                          objectFit: "contain",
                        }}
                      />
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: "600" }}>
                          Custom Icon Image
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Uploaded Image
                        </Typography>
                      </Box>
                    </>
                  ) : selectedCategory?.icon ? (
                    // Display Material-UI icon
                    <>
                      {getIconComponent(selectedCategory.icon)}
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: "600" }}>
                          {selectedCategory.icon}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Material-UI Icon
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No Icon Set
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Thumbnail Images */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Thumbnail Images
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={2}>
                  {selectedCategory?.images?.length > 0 ? (
                    selectedCategory.images.map((img, idx) => (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <Box
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            transition: "transform 0.2s",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <LazyLoadImage
                            alt={`thumbnail-${idx}`}
                            effect="blur"
                            src={img}
                            style={{
                              width: "100%",
                              height: "120px",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        No thumbnail images available
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Banner Images */}
              <Box mb={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Banner Images
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={2}>
                  {selectedCategory?.bannerImages?.length > 0 ? (
                    selectedCategory.bannerImages.map((img, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            transition: "transform 0.2s",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.03)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <LazyLoadImage
                            alt={`banner-${idx}`}
                            effect="blur"
                            src={img}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        No banner images available
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Banner Videos */}
              {selectedCategory?.bannerVideos?.length > 0 && (
                <Box mb={3}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: "600", color: "#333" }}
                  >
                    Banner Videos
                  </Typography>
                  <Divider style={{ marginBottom: "16px" }} />
                  <Grid container spacing={2}>
                    {selectedCategory.bannerVideos.map((vid, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Box
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <video
                            controls
                            style={{
                              width: "100%",
                              height: "200px",
                              borderRadius: "12px",
                            }}
                            src={vid}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Subcategories */}
              {selectedCategory?.children?.length > 0 && (
                <Box mb={3}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: "600", color: "#333" }}
                  >
                    Subcategories ({selectedCategory.children.length})
                  </Typography>
                  <Divider style={{ marginBottom: "16px" }} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedCategory.children.map((subCat, idx) => (
                      <Chip
                        key={idx}
                        label={subCat.name}
                        color="primary"
                        variant="outlined"
                        style={{
                          fontSize: "14px",
                          padding: "8px 4px",
                          fontWeight: "500",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px", background: "#f9f9f9" }}>
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            style={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Category;

